// Fee Management Form Validation Utilities

import { UpdateGlobalSettingsPayload, UpdateVendorFeePayload } from '../types';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Global settings validation
export const validateGlobalSettings = (data: UpdateGlobalSettingsPayload): ValidationResult => {
  const errors: ValidationError[] = [];

  // Commission rate validation
  if (data.defaultCommissionRate < 0 || data.defaultCommissionRate > 100) {
    errors.push({
      field: 'defaultCommissionRate',
      message: 'Commission rate must be between 0% and 100%'
    });
  }

  if (data.defaultCommissionRate === 0) {
    errors.push({
      field: 'defaultCommissionRate',
      message: 'Commission rate cannot be 0%'
    });
  }

  // Base fee validation
  if (data.defaultBaseFee < 0) {
    errors.push({
      field: 'defaultBaseFee',
      message: 'Base fee cannot be negative'
    });
  }

  if (data.defaultBaseFee > 10000) {
    errors.push({
      field: 'defaultBaseFee',
      message: 'Base fee seems too high (max ₦10,000)'
    });
  }

  // Fee per KM validation
  if (data.defaultFeePerKM < 0) {
    errors.push({
      field: 'defaultFeePerKM',
      message: 'Fee per KM cannot be negative'
    });
  }

  if (data.defaultFeePerKM > 500) {
    errors.push({
      field: 'defaultFeePerKM',
      message: 'Fee per KM seems too high (max ₦500)'
    });
  }

  // Free distance validation
  if (data.defaultFreeDistance < 0) {
    errors.push({
      field: 'defaultFreeDistance',
      message: 'Free distance cannot be negative'
    });
  }

  if (data.defaultFreeDistance > 50) {
    errors.push({
      field: 'defaultFreeDistance',
      message: 'Free distance seems too high (max 50km)'
    });
  }

  // Service charge validation
  if (data.defaultServiceChargeValue < 0) {
    errors.push({
      field: 'defaultServiceChargeValue',
      message: 'Service charge cannot be negative'
    });
  }

  if (data.defaultServiceChargeType === 'percentage' && data.defaultServiceChargeValue > 20) {
    errors.push({
      field: 'defaultServiceChargeValue',
      message: 'Service charge percentage seems too high (max 20%)'
    });
  }

  if (data.defaultServiceChargeType === 'fixed' && data.defaultServiceChargeValue > 2000) {
    errors.push({
      field: 'defaultServiceChargeValue',
      message: 'Fixed service charge seems too high (max ₦2,000)'
    });
  }

  // Transaction fee validation
  if (data.transactionFee < 0) {
    errors.push({
      field: 'transactionFee',
      message: 'Transaction fee cannot be negative'
    });
  }

  if (data.transactionFee > 1000) {
    errors.push({
      field: 'transactionFee',
      message: 'Transaction fee seems too high (max ₦1,000)'
    });
  }

  // Setup fee validation
  if (data.setupFee < 0) {
    errors.push({
      field: 'setupFee',
      message: 'Setup fee cannot be negative'
    });
  }

  // Monthly fee validation
  if (data.monthlyFee < 0) {
    errors.push({
      field: 'monthlyFee',
      message: 'Monthly fee cannot be negative'
    });
  }

  if (data.monthlyFee > 50000) {
    errors.push({
      field: 'monthlyFee',
      message: 'Monthly fee seems too high (max ₦50,000)'
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Vendor specific fee validation
export const validateVendorFee = (data: UpdateVendorFeePayload): ValidationResult => {
  const errors: ValidationError[] = [];

  // Restaurant ID validation
  if (!data.restaurantId || data.restaurantId.trim() === '') {
    errors.push({
      field: 'restaurantId',
      message: 'Restaurant ID is required'
    });
  }

  // Commission rate validation (if provided)
  if (data.commissionRate !== undefined) {
    if (data.commissionRate < 0 || data.commissionRate > 100) {
      errors.push({
        field: 'commissionRate',
        message: 'Commission rate must be between 0% and 100%'
      });
    }

    if (data.commissionRate === 0) {
      errors.push({
        field: 'commissionRate',
        message: 'Commission rate cannot be 0%'
      });
    }
  }

  // Base fee validation (if provided)
  if (data.baseFee !== undefined && data.baseFee < 0) {
    errors.push({
      field: 'baseFee',
      message: 'Base fee cannot be negative'
    });
  }

  // Fee per KM validation (if provided)
  if (data.feePerKM !== undefined && data.feePerKM < 0) {
    errors.push({
      field: 'feePerKM',
      message: 'Fee per KM cannot be negative'
    });
  }

  // Free distance validation (if provided)
  if (data.freeDistance !== undefined && data.freeDistance < 0) {
    errors.push({
      field: 'freeDistance',
      message: 'Free distance cannot be negative'
    });
  }

  // Service charge validation (if provided)
  if (data.serviceChargeValue !== undefined) {
    if (data.serviceChargeValue < 0) {
      errors.push({
        field: 'serviceChargeValue',
        message: 'Service charge cannot be negative'
      });
    }

    if (data.serviceChargeType === 'percentage' && data.serviceChargeValue > 20) {
      errors.push({
        field: 'serviceChargeValue',
        message: 'Service charge percentage seems too high (max 20%)'
      });
    }
  }

  // Change reason validation
  if (!data.changeReason || data.changeReason.trim() === '') {
    errors.push({
      field: 'changeReason',
      message: 'Change reason is required'
    });
  }

  if (data.changeReason && data.changeReason.length < 10) {
    errors.push({
      field: 'changeReason',
      message: 'Change reason must be at least 10 characters'
    });
  }

  if (data.changeReason && data.changeReason.length > 500) {
    errors.push({
      field: 'changeReason',
      message: 'Change reason cannot exceed 500 characters'
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Search validation
export const validateSearchQuery = (query: string): ValidationResult => {
  const errors: ValidationError[] = [];

  if (query.length < 2) {
    errors.push({
      field: 'search',
      message: 'Search query must be at least 2 characters'
    });
  }

  if (query.length > 100) {
    errors.push({
      field: 'search',
      message: 'Search query cannot exceed 100 characters'
    });
  }

  // Check for potentially harmful characters
  const harmfulPattern = /[<>'"&]/g;
  if (harmfulPattern.test(query)) {
    errors.push({
      field: 'search',
      message: 'Search query contains invalid characters'
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Number input validation
export const validateNumberInput = (
  value: number | string,
  fieldName: string,
  min?: number,
  max?: number,
  required?: boolean
): ValidationResult => {
  const errors: ValidationError[] = [];
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (required && (value === '' || value === null || value === undefined)) {
    errors.push({
      field: fieldName,
      message: `${fieldName} is required`
    });
    return { isValid: false, errors };
  }

  if (value !== '' && value !== null && value !== undefined) {
    if (isNaN(numValue)) {
      errors.push({
        field: fieldName,
        message: `${fieldName} must be a valid number`
      });
    } else {
      if (min !== undefined && numValue < min) {
        errors.push({
          field: fieldName,
          message: `${fieldName} must be at least ${min}`
        });
      }

      if (max !== undefined && numValue > max) {
        errors.push({
          field: fieldName,
          message: `${fieldName} cannot exceed ${max}`
        });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Email validation (for notifications)
export const validateEmail = (email: string): ValidationResult => {
  const errors: ValidationError[] = [];
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!email || email.trim() === '') {
    errors.push({
      field: 'email',
      message: 'Email is required'
    });
  } else if (!emailPattern.test(email)) {
    errors.push({
      field: 'email',
      message: 'Please enter a valid email address'
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Utility to get error message for a specific field
export const getFieldError = (errors: ValidationError[], fieldName: string): string | null => {
  const error = errors.find(err => err.field === fieldName);
  return error ? error.message : null;
};

// Utility to check if a field has an error
export const hasFieldError = (errors: ValidationError[], fieldName: string): boolean => {
  return errors.some(err => err.field === fieldName);
};

// Format validation errors for display
export const formatValidationErrors = (errors: ValidationError[]): string => {
  if (errors.length === 0) return '';
  
  if (errors.length === 1) {
    return errors[0].message;
  }

  return errors.map((error, index) => `${index + 1}. ${error.message}`).join('\n');
};